import React from 'react';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Table, Popconfirm, Tooltip, Popover } from 'antd';
import {
  CloseOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import H3FormInput from '@Uilib/h3FormInput';
import { v4 as uuidv4 } from 'uuid';
import { QUANTITY } from '@Apis/constants';
import H3Text from '@Uilib/h3Text';
import ProductFilterV2 from '@Components/Common/ProductFilterV2';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import ProductCategoryLabel from '@Components/Common/ProductCategoryLabel';
import HideValue from '../../../../../Common/RestrictedAccess/HideValue';
import './style.scss';

const RawMaterial = (props) => {
  const {
    data, cfRMLine, customLineInputChange, formSubmitted, visibleColumns, selectedTenant, user, priceMasking, handleMultiProductChange, loading, enableAdvanceSearch, finishedGoods
  } = props;
  const { isDataMaskingPolicyEnable, isHideCostPrice, isHideSellingPrice } = priceMasking;
  const getColumns = (isChild) => {
    const { trackWastage } = props;
    const columns = [
      {
        title: '',
        fixed: 'left',
        render: (record) => (
          <div style={{ whiteSpace: 'nowrap', minWidth: '40px', paddingLeft: '8px' }}>
            {`${record?.parentSqn ? `${record?.parentSqn}.` : ''}${record?.sqn}`}
          </div>
        ),
        width: '40px',
      },
      {
        title: 'Product',
        width: '240px',
        fixed: 'left',
        render: (text, record) => {
          const {
            handleProductChangeValue,
            handleProductChange,
            formSubmitted,
            addNewRow,
            tenantDepartmentId,
            data,
            excludedProducts,
          } = props;
          return (
            <div className="mo-rw-table__product-name">
              {record?.bomLineId || record?.mo_line_id ? (
                <React.Fragment>
                  <div className="flex-align-center-justify-between">
                    <div>
                      <Link to={`/inventory/product/view/${record?.tenant_product_info?.product_sku_id}`} target="_blank">
                        {`#${record?.tenant_product_info?.internal_sku_code}`}
                      </Link>
                      <div>
                        {`${record?.tenant_product_info?.ref_product_code ? `${record?.tenant_product_info?.ref_product_code} - ` : ''}${record?.productSkuName}`?.trim()}
                      </div>
                    </div>
                    {!record.tenant_product_info.tenant_product_id ? (
                      <div style={{ pointerEvents: 'all' }}>
                        <Tooltip title="This product is not active in selected Business Unit">
                          <InfoCircleOutlined
                            style={{
                              color: '#1677ff',
                              cursor: 'pointer',
                              fontSize: '14px',
                            }}
                          />
                        </Tooltip>
                      </div>
                    ) : ''}
                  </div>
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <ProductFilterV2
                    record={record}
                    productTypes={['STORABLE', 'SERVICE', 'NON_STORABLE']}
                    handleProductChangeValue={handleProductChangeValue}
                    handleProductChange={handleProductChange}
                    required
                    showError={formSubmitted && !record.productSkuName}
                    onClear={(key) => {
                      const { updateData } = props;
                      const copyData = JSON.parse(JSON.stringify(data));
                      const newData = copyData.map((item) => {
                        if (item.key === key) {
                          return {
                            key: uuidv4(),
                            asset1: '',
                            productSkuName: '',
                            product_sku_id: null,
                            product_sku_info: null,
                            quantity: '',
                            unitPrice: '',
                            taxId: '',
                            lot: '',
                          };
                        }
                        return {
                          ...item,
                        };
                      });
                      addNewRow();
                      updateData(newData);
                    }}
                    excludedProducts={excludedProducts}
                    tenantDepartmentId={tenantDepartmentId}
                    disabled={record?.product_sku_id}
                    showClear={!!record?.product_sku_id}
                    selectedTenant={selectedTenant}
                    enableAdvanceSearch={enableAdvanceSearch}
                    handleMultiProductChange={handleMultiProductChange}
                    loading={loading}
                  />
                </React.Fragment>
              )}
              {record?.product_category_info?.category_path?.length > 0 && (
                <ProductCategoryLabel
                  categoryPath={record?.product_category_info?.category_path}
                  categoryName={record?.product_category_info?.category_path?.at(-1)}
                  containerStyle={{
                    width: 'fit-content',
                  }}
                />
              )}
            </div>
          );
        },
        visible: visibleColumns?.PRODUCT?.visible,
      },
      {
        title: 'Current Stock',
        width: '120px',
        dataIndex: 'quantity',
        render: (text, record) => `${QUANTITY(
          ((record?.currentStock * record?.product_info?.uom_info?.ratio) / record?.uomInfo?.ratio) || '0',
          record?.uomInfo?.precision,
        )} ${record?.uomInfo?.uqc?.toProperCase() || ''}`,
        visible: visibleColumns?.CURRENT_STOCK?.visible,
      },
      {
        title: 'Estimated Usage',
        width: '173px',
        render: (text, record) => {
          const {
            createMOLoading,
            updateMOLoading,
            formSubmitted,
            handleQuantityChange,
            multipleFG,
          } = props;
          return (
            <div style={{ marginLeft: '5px' }}>
              {`${QUANTITY(record.estimatedUsage || 0, record?.tenant_product_info?.uom_info?.precision || 3)} ${record?.tenant_product_info?.uom_info?.uqc?.toProperCase() || 'N/A'}`}
            </div>
          )
        },
        visible: visibleColumns?.ESTIMATED_USAGE?.visible,
      },
      {
        title: 'Finished Good',
        width: '173px',
        render: (text, record) => {
          return (
            <React.Fragment>
              <div>
                <div>
                  <Link to={`/inventory/product/view/${record?.fg_product_info?.[0]?.product_sku_id}`}>
                    {`#${record?.fg_product_info?.[0]?.internal_sku_code}`} {`${record?.fg_product_info?.[0]?.ref_product_code ? `${record?.fg_product_info?.[0]?.ref_product_code} - ` : ''}${record?.fg_product_info?.[0]?.alias_name}`?.trim()}
                  </Link>
                </div>
                {record?.fg_product_info?.length > 1 && (
                    <Popover
                      content={record.fg_product_info.slice(1).map(fg => (
                        <div key={fg?.tenant_product_id}>
                          <div className='ellipses'>
                            <Link to={`/inventory/product/view/${fg?.product_sku_id}`}>
                              {`#${fg?.internal_sku_code}`} {`${fg?.ref_product_code ? `${fg?.ref_product_code} - ` : ''}${fg?.alias_name}`?.trim()}
                            </Link>  
                          </div>
                        </div>
                      ))}
                      trigger="click"
                      title="Other Finished Goods"
                    >
                      <div 
                        style={{
                        cursor: 'pointer',
                        color: 'grey',
                        }}
                      >
                        and {record.fg_product_info.length - 1} more...
                      </div>
                  </Popover>
                )}
                {!record.fg_product_info?.[0]?.tenant_product_id ? (
                  <div style={{ pointerEvents: 'all' }}>
                    <Tooltip title="This product is not active in selected Business Unit">
                      <InfoCircleOutlined
                        style={{
                          color: '#1677ff',
                          cursor: 'pointer',
                          fontSize: '14px',
                        }}
                      />
                    </Tooltip>
                  </div>
                ) : ''}
              </div>
            </React.Fragment>
          )
        },
        visible: finishedGoods?.length > 1,
      },
    ];
    if (trackWastage) {
      columns.push({
        title: 'Estimated Overage', // -> Estimated Wastage
        width: '173px',
        render: (text, record) => {
          const {
            createMOLoading,
            updateMOLoading,
            formSubmitted,
            handleQuantityChange,
            multipleFG,
          } = props;
          return (
            <div style={{ marginLeft: '5px' }}>
              {`${QUANTITY(record.wastage_quantity || 0, record?.tenant_product_info?.uom_info?.precision || 3)} ${record?.tenant_product_info?.uom_info?.uqc?.toProperCase() || 'N/A'}`}
            </div>
          )
        },
        visible: visibleColumns?.ESTIMATED_WASTAGE?.visible,
      });
    }
    columns.push(
      {
        title: 'Unit Cost',
        render: (text, record) => ((isDataMaskingPolicyEnable & isHideCostPrice) ? <HideValue showPopOver popOverMessage={"You don't have access to view unit cost"} /> : `${props.MONEY(record?.costPerUnit || 0)}`),
        visible: visibleColumns?.UNIT_COST?.visible,
      },
      {
        title: 'Estimated Total Cost',
        fixed: 'right',
        render: (text, record) => ((isDataMaskingPolicyEnable & isHideCostPrice) ? <HideValue showPopOver popOverMessage={"You don't have access to view unit cost"} /> : 
        `${props.MONEY(record?.totalEstimatedCost || 0)}`),
        visible: visibleColumns?.ESTIMATED_TOTAL_COST?.visible,
      },
    );

    return columns?.filter((item) => item?.visible);
  };

  const getDataSource = () => {
    if (data) {
      return JSON.parse(JSON.stringify(data));
    }
    return [];
  };

  return (
    <React.Fragment>
      <div className="mo-rw-table-wrapper hide__in-mobile">
        <Table
          bordered
          loading={loading}
          showHeader
          size="small"
          scroll={{ x: 'max-content' }}
          columns={getColumns(false)}
          dataSource={getDataSource()?.map((item, index) => ({ ...item, sqn: index + 1 }))}
          pagination={false}
          rowClassName={(record) => (record?.product_sku_id && !record.tenant_product_info?.tenant_product_id ? 'disabled-mo-rw-table-row' : '')}
        />
      </div>
    </React.Fragment>
  );
};

const mapStateToProps = ({
  UserReducers, PurchaseOrderReducers, OfferReducers,
}) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  priceMasking: UserReducers.priceMasking,
});

/**
 *
 * @return {{}}
 */
const mapDispatchToProps = () => ({

});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(RawMaterial));
